import { useMemo } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bookmark, ExternalLink, ImageOff, Loader2 } from "lucide-react";
import { MarketingPageLayout } from "@/components/marketing/MarketingPageLayout";
import { GetStartedLink, GetStashtCta } from "@/components/marketing/GetStashtCta";
import { StoreBadges } from "@/components/shared/StoreBadges";
import { StashThumbnail, type StashThumbnailData } from "@/components/shared/StashThumbnail";
import { PageMeta } from "@/components/seo/PageMeta";
import { FaqDisclosureList } from "@/components/shared/FaqDisclosureList";
import type { FaqEntry } from "@/lib/structuredData";
import { USE_CASE_CONFIGS, type UseCaseConfig } from "@/pages/marketing/useCaseConfigs";
import { postsService, type Post } from "@/services/posts.service";
import { BLOG, STASH_DETAIL } from "@/routes";

type UseCaseExample = NonNullable<UseCaseConfig["examples"]>["cards"][number];

const USE_CASE_STASH_VISUAL_ASPECT = "aspect-[4/5] sm:aspect-auto sm:h-full sm:min-h-[240px]";

const toThumbnailData = (post: Post): StashThumbnailData => {
  const enrichment = post as Post & { main_enrichment_response?: StashThumbnailData["main_enrichment_response"] };

  return {
    id: post.stasht_id ?? post.id,
    title: post.title,
    user_title: post.user_title,
    content: post.content as StashThumbnailData["content"],
    platform: post.platform ?? post.source?.platform,
    source: post.source as StashThumbnailData["source"],
    main_enrichment_response: enrichment.main_enrichment_response,
    unfurl_status: post.unfurl_status,
  };
};

const FaqSection = ({ entries }: { entries: ReadonlyArray<FaqEntry> }) => (
  <section className="flex flex-col gap-6">
    <h2 className="text-3xl md:text-4xl font-semibold text-foreground m-0">
      Frequently Asked <span className="text-brand-500">Questions</span>
    </h2>
    <FaqDisclosureList
      name="use-case-faq"
      items={entries.map(entry => ({ id: entry.question, question: entry.question, answer: entry.answer }))}
      questionClassName="text-lg font-semibold"
      answerClassName="text-base text-foreground/80"
    />
  </section>
);

const UseCaseExampleVisual = ({
  card,
  post,
  isLoading,
  isError,
}: {
  card: UseCaseExample;
  post?: Post;
  isLoading: boolean;
  isError: boolean;
}) => {
  if (post) {
    return (
      <StashThumbnail
        post={toThumbnailData(post)}
        href={STASH_DETAIL({ stashId: card.id })}
        width="full"
        cornerRadius="lg"
        aspectRatio={USE_CASE_STASH_VISUAL_ASPECT}
        showTitle
        loading="lazy"
        className="h-full w-full"
      />
    );
  }

  return (
    <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-foreground/15 bg-muted/30 text-center text-xs text-foreground/50 sm:aspect-auto sm:h-full sm:min-h-[240px]">
      {isLoading ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading visual
        </>
      ) : (
        <>
          <ImageOff className="h-5 w-5" />
          {isError ? "Visual failed to load" : "Visual unavailable"}
        </>
      )}
    </div>
  );
};

const UseCaseExamplesSection = ({ examples }: { examples: NonNullable<UseCaseConfig["examples"]> }) => {
  const stashIds = useMemo(() => examples.cards.map(card => card.id), [examples.cards]);

  const { data: postsMap, isLoading, isError } = useQuery({
    queryKey: ["use-case-stash-examples", stashIds],
    queryFn: async () => {
      const response = await postsService.batchGetPosts(stashIds, true);
      return response.posts ?? {};
    },
    enabled: stashIds.length > 0,
    staleTime: 5 * 60_000,
  });

  return (
    <section className="flex flex-col gap-6">
      <div className="mx-auto flex reading-column flex-col gap-3 text-center">
        <h2 className="m-0 text-3xl font-semibold text-foreground md:text-4xl">{examples.heading}</h2>
        <p className="m-0 text-lg text-foreground/75">{examples.body}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {examples.cards.map(card => {
          const post = postsMap?.[card.id];

          return (
            <article key={card.id} className="rounded-lg border border-foreground/10 bg-card p-4 shadow-sm">
              <div className="grid h-full gap-4 sm:grid-cols-[132px_minmax(0,1fr)] sm:items-stretch">
                <UseCaseExampleVisual card={card} post={post} isLoading={isLoading} isError={isError} />
                <div className="flex min-w-0 flex-col gap-2">
                  <p className="m-0 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-500">
                    <Bookmark className="h-3.5 w-3.5" />
                    Saved on Stasht
                  </p>
                  <div className="flex flex-col gap-1">
                    <h3 className="m-0 text-lg font-semibold leading-snug text-foreground">{card.title}</h3>
                    <p className="m-0 text-sm leading-relaxed text-foreground/70">{card.description}</p>
                  </div>
                  {(card.sourceLabel || card.platform) && (
                    <p className="m-0 text-xs text-foreground/50">
                      {[card.sourceLabel, card.platform].filter(Boolean).join(" - ")}
                    </p>
                  )}
                  {card.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {card.tags.map(tag => (
                        <span
                          key={tag}
                          className="rounded-full border border-foreground/10 bg-background px-2 py-0.5 text-xs text-foreground/60"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <Link
                    to={STASH_DETAIL({ stashId: card.id })}
                    className="mt-auto inline-flex w-fit items-center justify-center gap-2 rounded-md border border-foreground/10 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:border-brand-500/40 hover:text-brand-500"
                  >
                    Open stash
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

/**
 * Shared template for SEO use-case landing pages. Section order follows the
 * website channel brief (docs/content/guidance/channels/website.md):
 * hero → problem → solution blocks → how it works → FAQ → related → CTA.
 * Copy lives in useCaseConfigs.tsx.
 */
const UseCasePageContent = ({ config }: { config: UseCaseConfig }) => (
  <MarketingPageLayout surface="landing">
    <PageMeta
      title={config.metaTitle}
      description={config.metaDescription}
      path={config.path}
      ogImage={config.ogImage}
      jsonLd={config.jsonLd}
    />
    <div className="page-frame page-content flex flex-col gap-14 md:gap-20">
      {/* Hero */}
      <header className="flex flex-col items-center gap-5 text-center">
        <h1 className="page-title text-foreground text-balance m-0 reading-column">
          {config.heroTitle}
        </h1>
        <p className="text-lg md:text-xl text-foreground/70 text-balance m-0 reading-column">{config.heroSubhead}</p>
        <div className="flex items-center gap-4 mt-2">
          <StoreBadges surface="landing" placement="hero" />
          {config.showWebStart && <GetStartedLink surface="landing" placement="hero" />}
        </div>
        <p className="text-sm text-foreground/50 m-0">Free on iOS, Android, and desktop.</p>
      </header>

      {/* Problem */}
      <section className="flex flex-col gap-4 reading-column mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-semibold text-foreground text-balance m-0">{config.problem.heading}</h2>
        {config.problem.paragraphs.map(paragraph => (
          <p key={paragraph.slice(0, 32)} className="text-lg text-foreground/75 text-pretty m-0">
            {paragraph}
          </p>
        ))}
      </section>

      {/* Solution blocks */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {config.solutions.map(solution => (
          <div
            key={solution.heading}
            className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-6 flex flex-col gap-2"
          >
            <h3 className="text-xl font-semibold text-foreground text-balance m-0">{solution.heading}</h3>
            <p className="text-foreground/75 text-pretty m-0">{solution.body}</p>
          </div>
        ))}
      </section>

      {config.featureSpotlight && (
        <section className="grid grid-cols-1 items-center gap-8 md:grid-cols-[minmax(0,1fr)_minmax(220px,320px)]">
          <div className="flex flex-col gap-3 text-center md:text-left">
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground text-balance m-0">
              {config.featureSpotlight.heading}
            </h2>
            <p className="text-lg text-foreground/75 text-pretty m-0">{config.featureSpotlight.body}</p>
            {config.featureSpotlight.links && (
              <ul className="m-0 hidden list-none flex-wrap gap-x-6 gap-y-2 p-0 md:flex">
                {config.featureSpotlight.links.map(link => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 font-medium text-brand-500 hover:underline"
                    >
                      {link.label}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <figure className="m-0 flex flex-col items-center gap-3 md:items-end">
            <img
              src={config.featureSpotlight.imageSrc}
              alt={config.featureSpotlight.imageAlt}
              loading="lazy"
              width={800}
              height={1422}
              className="aspect-[800/1422] w-full max-w-[260px] rounded-2xl border border-foreground/10 object-cover shadow-[0_24px_60px_rgba(15,23,42,0.16)] sm:max-w-[300px]"
            />
            <figcaption className="max-w-[300px] text-center text-sm text-foreground/50 md:text-right">
              {config.featureSpotlight.caption}
            </figcaption>
          </figure>
        </section>
      )}

      {config.examples && <UseCaseExamplesSection examples={config.examples} />}

      {/* How it works */}
      <section className="flex flex-col gap-8">
        <h2 className="text-3xl md:text-4xl font-semibold text-foreground text-balance m-0 text-center">
          How it works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {config.steps.map((step, index) => (
            <div key={step.title} className="flex flex-col gap-2 items-center text-center">
              <span className="flex items-center justify-center size-10 rounded-full bg-brand-500 text-white font-bold text-lg">
                {index + 1}
              </span>
              <h3 className="text-lg font-semibold text-foreground text-balance m-0">{step.title}</h3>
              <p className="text-foreground/70 text-pretty m-0">{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      <FaqSection entries={config.faq} />

      {/* Related reading */}
      <section className="flex flex-col gap-3">
        <h2 className="text-xl font-semibold text-foreground m-0">Keep reading</h2>
        <ul className="m-0 p-0 list-none flex flex-col gap-2">
          {config.related.map(link => (
            <li key={link.to}>
              <Link to={link.to} className="text-brand-500 font-medium hover:underline">
                {link.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <GetStashtCta surface="landing" showWebStart={config.showWebStart} />
    </div>
  </MarketingPageLayout>
);

const UseCasePage = () => {
  const { pathname } = useLocation();
  const config = USE_CASE_CONFIGS[pathname];

  if (!config) {
    return <Navigate to={BLOG()} replace />;
  }

  return <UseCasePageContent config={config} />;
};

export default UseCasePage;
